import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import { Navbar } from "../../components/navbar/Navbar";
import { Footer } from "../../components/footer/Footer";
import { getEmployee } from "../../models/employees";
// Layout/card/detail-list styles are shared with the confirmation page.
import "../ReservationConfirmed/reservationConfirmed.css";
import "./cancelReservation.css";

const ERROR_MESSAGES = {
  BAD_TOKEN:
    "Tento odkaz není platný. Zkontroluj prosím, že jsi otevřel celý odkaz z emailu.",
  NOT_FOUND:
    "Rezervaci se nepodařilo najít. Možná už byla zrušena nebo je odkaz neplatný.",
  BAD_METHOD:
    "Něco se pokazilo na naší straně. Zkus to prosím znovu nebo nás kontaktuj na 777 606 447.",
  SERVER_ERROR:
    "Něco se pokazilo na naší straně. Zkus to prosím znovu nebo nás kontaktuj na 777 606 447.",
};

const ReservationDetails = ({ reservation }) => {
  const employee = getEmployee(parseInt(reservation.zamestnanecId, 10));
  const formattedDate = dayjs(reservation.datum).format("DD.MM.YYYY");
  const formattedTime = dayjs(reservation.zacatek_cas, "HH:mm:ss").format(
    "HH:mm",
  );

  return (
    <ul className="confirmDetailsList">
      <li>
        <span className="confirmDetailLabel">Barber:</span>
        <span className="confirmDetailValue">
          {employee ? employee.name : "—"}
        </span>
      </li>
      <li>
        <span className="confirmDetailLabel">Datum:</span>
        <span className="confirmDetailValue">{formattedDate}</span>
      </li>
      <li>
        <span className="confirmDetailLabel">Čas:</span>
        <span className="confirmDetailValue">{formattedTime}</span>
      </li>
      <li>
        <span className="confirmDetailLabel">Služba:</span>
        <span className="confirmDetailValue">{reservation.klient_sluzba}</span>
      </li>
      {reservation.klient_sluzby_dodatecne && (
        <li>
          <span className="confirmDetailLabel">Extra služby:</span>
          <span className="confirmDetailValue">
            {reservation.klient_sluzby_dodatecne}
          </span>
        </li>
      )}
      <li>
        <span className="confirmDetailLabel">Cena:</span>
        <span className="confirmDetailValue">{reservation.klient_cena} Kč</span>
      </li>
    </ul>
  );
};

export const CancelReservation = () => {
  const [searchParams] = useSearchParams();
  // "loading" | "confirm" | "cancelling" | "cancelled" | "error"
  const [status, setStatus] = useState("loading");
  const [reservation, setReservation] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const token = searchParams.get("token");

  // Preview only — this GET never mutates anything server-side, so React 18
  // StrictMode's double mount is harmless.
  useEffect(() => {
    if (!token) {
      setErrorCode("BAD_TOKEN");
      setStatus("error");
      return;
    }

    let cancelled = false;
    const loadPreview = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL_CANCEL}?token=${encodeURIComponent(token)}`,
        );
        if (cancelled) return;
        if (response.data && response.data.success) {
          if (response.data.alreadyCancelled) {
            setStatus("cancelled");
          } else {
            setReservation(response.data.reservation);
            setStatus("confirm");
          }
        } else {
          setErrorCode((response.data && response.data.code) || "SERVER_ERROR");
          setStatus("error");
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Cancel reservation preview failed:", err);
        setErrorCode("SERVER_ERROR");
        setStatus("error");
      }
    };

    loadPreview();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleConfirmCancel = async () => {
    setModalOpen(false);
    setStatus("cancelling");
    try {
      const response = await axios.post(process.env.REACT_APP_API_URL_CANCEL, {
        token,
      });
      if (response.data && response.data.success) {
        setStatus("cancelled");
      } else {
        console.error("Cancel reservation rejected:", response.data);
        setErrorCode((response.data && response.data.code) || "SERVER_ERROR");
        setStatus("error");
      }
    } catch (err) {
      console.error("Cancel reservation failed:", err);
      setErrorCode("SERVER_ERROR");
      setStatus("error");
    }
  };

  return (
    <div className="reservationConfirmed cancelReservation">
      <div className="pageWrapper">
        <Navbar />
        <div className="bodyContent">
          <div className="confirmCard">
            {status === "loading" && (
              <div className="confirmLoadingState">
                <div className="confirmSpinner" />
                <h2>Načítáme tvoji rezervaci…</h2>
              </div>
            )}

            {status === "cancelling" && (
              <div className="confirmLoadingState">
                <div className="confirmSpinner" />
                <h2>Rušíme tvoji rezervaci…</h2>
              </div>
            )}

            {status === "confirm" && reservation && (
              <div className="cancelConfirmState">
                <div className="cancelWarnIcon">!</div>
                <h2>Zrušit rezervaci?</h2>
                <p className="confirmSubtitle">
                  Zkontroluj prosím detaily níže. Zrušení nelze vzít zpět —
                  termín se ihned uvolní pro ostatní.
                </p>
                <ReservationDetails reservation={reservation} />
                <button
                  type="button"
                  className="cancelDangerBtn"
                  onClick={() => setModalOpen(true)}
                >
                  Zrušit rezervaci
                </button>
                <Link to="/" className="cancelKeepLink">
                  Ponechat rezervaci
                </Link>
              </div>
            )}

            {status === "cancelled" && (
              <div className="cancelDoneState">
                <div className="confirmCheckIcon">✓</div>
                <h2>Rezervace zrušena</h2>
                <p className="confirmSubtitle">
                  Termín je uvolněn a barbera jsme informovali. Potvrzení jsme
                  ti poslali také na email.
                </p>
                <Link to="/rezervace" className="confirmRetryBtn">
                  Objednat se znovu
                </Link>
              </div>
            )}

            {status === "error" && (
              <div className="confirmErrorState">
                <div className="confirmErrorIcon">!</div>
                <h2>Rezervaci se nepodařilo zrušit</h2>
                <p className="confirmErrorMessage">
                  {ERROR_MESSAGES[errorCode] || ERROR_MESSAGES.SERVER_ERROR}
                </p>
                <Link to="/" className="confirmRetryBtn">
                  Zpět na úvod
                </Link>
              </div>
            )}
          </div>
        </div>
        <footer>
          <Footer />
        </footer>
      </div>

      {modalOpen && (
        <div
          className="cancelModalOverlay"
          onClick={() => setModalOpen(false)}
          role="presentation"
        >
          <div
            className="cancelModal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cancelModalTitle"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cancelModalTitle">Opravdu chceš zrušit rezervaci?</h3>
            <p>
              Tuto akci nelze vrátit zpět. Pokud rušíš méně než 2 hodiny před
              termínem, zavolej nám prosím navíc na 777 606 447.
            </p>
            <div className="cancelModalActions">
              <button
                type="button"
                className="cancelDangerBtn"
                onClick={handleConfirmCancel}
              >
                Ano, zrušit
              </button>
              <button
                type="button"
                className="cancelSecondaryBtn"
                onClick={() => setModalOpen(false)}
              >
                Ne, ponechat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
